import { getMonthName } from "../../../extensions/helper";

export const vidMap = {
  0: 'Абонементы',
  1: 'Пакеты'
};

export const vidOrder = [0, 1, 2, 3];

// Функции для работы с датами
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('ru-RU');
};

//Функция проверки соответствия формату даты
export const formatName = (name) => {
  // Если диапазон дат
  if (/^\d{2}\.\d{2}\.\d{4}-\d{2}\.\d{2}\.\d{4}$/.test(name)) {
    return name
      .split('-')
      .map(dateStr => {
        const [day, month, year] = dateStr.split('.');
        return `${day}.${month}.${year.slice(-2)}`;
      })
      .join('-');
  }
  
  // Если одиночная дата
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(name)) {
    const [day, month, year] = name.split('.');
    return `${day}.${month}.${year.slice(-2)}`;
  }

  // Иначе возвращаем как есть
  return name;
};

const formatWeek = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return `${start.toLocaleDateString('ru-RU')}-${end.toLocaleDateString('ru-RU')}`;
};

const formatMonth = (dateStr) => {
  const date = new Date(dateStr);
  return `${getMonthName(date.getMonth() + 1)} ${date.getFullYear()}`;
};

const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Понедельник как начало недели
  return new Date(d.setDate(diff));
};

const getWeekEnd = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? 0 : 7); // Воскресенье как конец недели
  return new Date(d.setDate(diff));
};

// Функция группировки данных по кассам (существующий режим)
const groupByKassa = (data) => {
  const kassas = Array.from(new Set(data.map(i => i.kassaName))).filter(Boolean);
  const rowMap = {};
  
  kassas.forEach(k => {
    rowMap[k] = { name: k };
    vidOrder.forEach(v => {
      rowMap[k][v] = { symaPut: 0, symaExp: 0 };
    });
    rowMap[k].total = 0;
  });
  
  data.forEach(item => {
    if (!rowMap[item.kassaName] || !vidOrder.includes(item.vid)) return;
    if (item.symaPut && item.symaPut !== 0) {
      rowMap[item.kassaName][item.vid].symaPut += item.symaPut;
      rowMap[item.kassaName].total += item.symaPut;
    }
    if (item.symaExp && item.symaExp !== 0) {
      rowMap[item.kassaName][item.vid].symaExp += item.symaExp;
      rowMap[item.kassaName].total -= item.symaExp;
    }
  });
  
  return {
    tableRows: kassas.map(k => rowMap[k]),
    rowLabels: kassas
  };
};

// Функция группировки данных по дням
const groupByDay = (data) => {
  const dates = Array.from(new Set(data.map(i => i.date?.slice(0, 10)))).filter(Boolean).sort();
  const rowMap = {};
  
  dates.forEach(date => {
    rowMap[date] = { name: formatDate(date) };
    vidOrder.forEach(v => {
      rowMap[date][v] = { symaPut: 0, symaExp: 0 };
    });
    rowMap[date].total = 0;
  });
  
  data.forEach(item => {
    const date = item.date?.slice(0, 10);
    if (!rowMap[date] || !vidOrder.includes(item.vid)) return;
    if (item.symaPut && item.symaPut !== 0) {
      rowMap[date][item.vid].symaPut += item.symaPut;
      rowMap[date].total += item.symaPut;
    }
    if (item.symaExp && item.symaExp !== 0) {
      rowMap[date][item.vid].symaExp += item.symaExp;
      rowMap[date].total -= item.symaExp;
    }
  });
  
  return {
    tableRows: dates.map(date => rowMap[date]),
    rowLabels: dates
  };
};

// Функция группировки данных по неделям
const groupByWeek = (data) => {
  const weekMap = new Map();
  
  data.forEach(item => {
    if (!item.date || !vidOrder.includes(item.vid)) return;
    const date = new Date(item.date);
    const weekStart = getWeekStart(date);
    const weekEnd = getWeekEnd(date);
    const weekKey = `${weekStart.toISOString().slice(0, 10)}_${weekEnd.toISOString().slice(0, 10)}`;
    const weekLabel = formatWeek(weekStart, weekEnd);
    
    if (!weekMap.has(weekKey)) {
      weekMap.set(weekKey, {
        name: weekLabel,
        key: weekKey,
        vid: {}
      });
      vidOrder.forEach(v => {
        weekMap.get(weekKey).vid[v] = { symaPut: 0, symaExp: 0 };
      });
      weekMap.get(weekKey).total = 0;
    }
    
    const week = weekMap.get(weekKey);
    if (item.symaPut && item.symaPut !== 0) {
      week.vid[item.vid].symaPut += item.symaPut;
      week.total += item.symaPut;
    }
    if (item.symaExp && item.symaExp !== 0) {
      week.vid[item.vid].symaExp += item.symaExp;
      week.total -= item.symaExp;
    }
  });
  
  const sortedWeeks = Array.from(weekMap.values()).sort((a, b) => a.key.localeCompare(b.key));
  
  return {
    tableRows: sortedWeeks.map(week => ({
      name: week.name,
      ...week.vid,
      total: week.total
    })),
    rowLabels: sortedWeeks.map(w => w.key)
  };
};

// Функция группировки данных по месяцам
const groupByMonth = (data) => {
  const monthMap = new Map();
  
  data.forEach(item => {
    if (!item.date || !vidOrder.includes(item.vid)) return;
    const date = new Date(item.date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const monthLabel = formatMonth(date);
    
    if (!monthMap.has(monthKey)) {
      monthMap.set(monthKey, {
        name: monthLabel,
        key: monthKey,
        vid: {}
      });
      vidOrder.forEach(v => {
        monthMap.get(monthKey).vid[v] = { symaPut: 0, symaExp: 0 };
      });
      monthMap.get(monthKey).total = 0;
    }
    
    const month = monthMap.get(monthKey);
    if (item.symaPut && item.symaPut !== 0) {
      month.vid[item.vid].symaPut += item.symaPut;
      month.total += item.symaPut;
    }
    if (item.symaExp && item.symaExp !== 0) {
      month.vid[item.vid].symaExp += item.symaExp;
      month.total -= item.symaExp;
    }
  });
  
  const sortedMonths = Array.from(monthMap.values()).sort((a, b) => a.key.localeCompare(b.key));
  
  return {
    tableRows: sortedMonths.map(month => ({
      name: month.name,
      ...month.vid,
      total: month.total
    })),
    rowLabels: sortedMonths.map(m => m.key)
  };
};

export const groupData = (data, reportType) => {
  if (!data.length) {
    return { tableRows: [], tableTotal: {}, rowLabels: [] };
  }
  
  let result;
  
  switch (reportType) {
    case 0:
      result = groupByKassa(data);
      break;
    case 1:
      result = groupByDay(data);
      break;
    case 7:
      result = groupByWeek(data);
      break;
    case 31:
      result = groupByMonth(data);
      break;
    default:
      result = groupByKassa(data);
  }
  
  const totalRow = { name: 'Всего' };
  vidOrder.forEach(v => {
    totalRow[v] = {
      symaPut: result.tableRows.reduce((acc, row) => acc + (row[v]?.symaPut || 0), 0),
      symaExp: result.tableRows.reduce((acc, row) => acc + (row[v]?.symaExp || 0), 0),
    };
  });
  
  return {
    ...result,
    tableTotal: totalRow
  };
};

export const getTableHeader = (reportType) => {
  switch (reportType) {
    case 0: return 'Сотрудник';
    case 1: return 'Менеджер';
    case 7: return 'Исполнитель';
    case 31: return 'Пользователь';
    default: return 'Сотрудник';
  }
};

export const handleCellClick = (data, rowName, vid, type, reportType, rowLabels, tableRows) => {
  if (!data.length) return null;

  if (vid === 'all' && type === 'total') {
    let filtered;
    if (rowName === 'all') {
      filtered = data;
      return {
        items: filtered,
        vidName: 'Все',
        vid: 'all',
        type: 'total',
        groupByKassa: false,
        reportType
      };
    } else {
      if (reportType === 0) {
        filtered = data.filter(item => item.kassaName === rowName);
        return {
          items: filtered,
          kassaName: rowName,
          vidName: 'Все',
          vid: 'all',
          type: 'total',
          groupByKassa: false
        };
      } else if (reportType === 1) {
        const date = rowLabels.find(label => formatDate(label) === rowName);
        if (date) {
          filtered = data.filter(item => item.date?.slice(0, 10) === date);
        }
        return {
          items: filtered,
          periodName: rowName,
          vidName: 'Все',
          vid: 'all',
          type: 'total',
          groupByKassa: false,
          reportType
        };
      } else if (reportType === 7) {
        const weekKey = rowLabels.find((label, index) => {
          const week = tableRows[index];
          return week && week.name === rowName;
        });
        if (weekKey) {
          const [startDate, endDate] = weekKey.split('_');
          filtered = data.filter(item => {
            const itemDate = new Date(item.date);
            const start = new Date(startDate);
            const end = new Date(endDate);
            return itemDate >= start && itemDate <= end;
          });
        }
        return {
          items: filtered,
          periodName: rowName,
          vidName: 'Все',
          vid: 'all',
          type: 'total',
          groupByKassa: false,
          reportType
        };
      } else if (reportType === 31) {
        const monthKey = rowLabels.find((label, index) => {
          const month = tableRows[index];
          return month && month.name === rowName;
        });
        if (monthKey) {
          const [year, month] = monthKey.split('-');
          filtered = data.filter(item => {
            const itemDate = new Date(item.date);
            return itemDate.getFullYear() === parseInt(year) &&
              itemDate.getMonth() === parseInt(month) - 1;
          });
        }
        return {
          items: filtered,
          periodName: rowName,
          vidName: 'Все',
          vid: 'all',
          type: 'total',
          groupByKassa: false,
          reportType
        };
      }
    }
  }

  let filteredData = [];
  
  if (reportType === 0) {
    if (rowName === 'all') {
      const grouped = rowLabels.map(kassa => ({
        kassaName: kassa,
        displayName: `${kassa} (${vidMap[vid]})`,
        items: data.filter(item => item.kassaName === kassa && item.vid === vid)
      })).filter(group => group.items.length > 0);
      return {
        items: grouped,
        vidName: vidMap[vid],
        vid: vid,
        type: type,
        groupByKassa: true
      };
    } else {
      filteredData = data.filter(item => 
        item.kassaName === rowName && 
        item.vid === vid
      );
      return {
        items: filteredData,
        kassaName: rowName,
        vidName: vidMap[vid],
        vid: vid,
        type: type,
        groupByKassa: false
      };
    }
  } else {
    if (rowName === 'all') {
      const grouped = rowLabels.map(label => {
        let periodName = '';
        let items = [];
        
        if (reportType === 1) {
          periodName = formatDate(label);
          items = data.filter(item => 
            item.date?.slice(0, 10) === label && 
            item.vid === vid
          );
        } else if (reportType === 7) {
          const [startDate, endDate] = label.split('_');
          periodName = formatWeek(startDate, endDate);
          items = data.filter(item => {
            const itemDate = new Date(item.date);
            const start = new Date(startDate);
            const end = new Date(endDate);
            return itemDate >= start && itemDate <= end && item.vid === vid;
          });
        } else if (reportType === 31) {
          const [year, month] = label.split('-');
          periodName = formatMonth(new Date(parseInt(year), parseInt(month) - 1, 1));
          items = data.filter(item => {
            const itemDate = new Date(item.date);
            return itemDate.getFullYear() === parseInt(year) && 
                   itemDate.getMonth() === parseInt(month) - 1 && 
                   item.vid === vid;
          });
        }
        
        return {
          periodName: periodName,
          displayName: `${periodName} (${vidMap[vid]})`,
          items: items
        };
      }).filter(group => group.items.length > 0);
      
      return {
        items: grouped,
        vidName: vidMap[vid],
        vid: vid,
        type: type,
        groupByKassa: false,
        reportType: reportType
      };
    } else {
      if (reportType === 1) {
        const date = rowLabels.find(label => formatDate(label) === rowName);
        if (date) {
          filteredData = data.filter(item => 
            item.date?.slice(0, 10) === date && 
            item.vid === vid
          );
        }
      } else if (reportType === 7) {
        const weekKey = rowLabels.find((label, index) => {
          const week = tableRows[index];
          return week && week.name === rowName;
        });
        if (weekKey) {
          const [startDate, endDate] = weekKey.split('_');
          filteredData = data.filter(item => {
            const itemDate = new Date(item.date);
            const start = new Date(startDate);
            const end = new Date(endDate);
            return itemDate >= start && itemDate <= end && item.vid === vid;
          });
        }
      } else if (reportType === 31) {
        const monthKey = rowLabels.find((label, index) => {
          const month = tableRows[index];
          return month && month.name === rowName;
        });
        if (monthKey) {
          const [year, month] = monthKey.split('-');
          filteredData = data.filter(item => {
            const itemDate = new Date(item.date);
            return itemDate.getFullYear() === parseInt(year) && 
                   itemDate.getMonth() === parseInt(month) - 1 && 
                   item.vid === vid;
          });
        }
      }
      
      if (filteredData.length > 0) {
        return {
          items: filteredData,
          periodName: rowName,
          vidName: vidMap[vid],
          vid: vid,
          type: type,
          groupByKassa: false,
          reportType: reportType
        };
      }
    }
  }
  
  return null;
};